/*
 * New mail without refreshing: the API holds IMAP IDLE on Inbox while this
 * stream is open and pushes counts and new messages. Nothing polls while it's
 * connected.
 */
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, reportSignedOut, wmApi } from "./api";
import { keys, type ListView } from "./data";
import type { Addr } from "./types";

export type NewMail = { uid: number; from: Addr | null; subject: string };

export function useLiveMail(mb: string, onNew: (messages: NewMail[]) => void): { connected: boolean; problem: string | null } {
  const qc = useQueryClient();
  const [state, setState] = useState<{ connected: boolean; problem: string | null }>({ connected: false, problem: null });
  const onNewRef = useRef(onNew);
  onNewRef.current = onNew;

  useEffect(() => {
    let es: EventSource | null = null;
    let disposed = false;
    let attempt = 0; // tries since the stream was last open
    let retry: ReturnType<typeof setTimeout> | undefined;
    // Folder lists and counts follow new mail; search results stay as they were (searching every folder
    // again for each arrival would hold up the mailbox's connection).
    const refresh = () => {
      void qc.invalidateQueries({ queryKey: keys.folders(mb) });
      void qc.invalidateQueries({ queryKey: keys.lists(mb), predicate: (q) => (q.queryKey[3] as ListView | undefined)?.kind !== "search" });
    };

    const connect = (resync: boolean) => {
      const stream = new EventSource(`/api/webmail/m/${mb}/events`);
      es = stream;
      let down = false;
      stream.onopen = () => {
        attempt = 0;
      };
      stream.addEventListener("status", (e) => {
        const d = JSON.parse((e as MessageEvent).data) as { connected: boolean; message?: string };
        setState({ connected: d.connected, problem: d.connected ? null : (d.message ?? "Lost the connection to the mail server. Trying again.") });
        if (d.connected && down) refresh(); // back after a drop: catch up
        down = !d.connected;
      });
      // The opening counts match what was just loaded, unless the stream was closed for a while
      // (the page sat in the back/forward cache): then anything that arrived meanwhile is fetched.
      let first = !resync;
      stream.addEventListener("counts", () => {
        if (first) {
          first = false;
          return;
        }
        refresh();
      });
      stream.addEventListener("new", (e) => {
        const d = JSON.parse((e as MessageEvent).data) as { messages: NewMail[] };
        refresh();
        onNewRef.current(d.messages);
      });
      stream.addEventListener("mail_auth", () => {
        stream.close();
        reportSignedOut(mb);
      });
      stream.addEventListener("signed_out", () => {
        stream.close();
        void qc.invalidateQueries({ queryKey: ["wm", "session"] });
      });
      stream.onerror = () => {
        // EventSource reconnects by itself after a dropped connection, but an error answer closes it for good:
        // a 401 or 403, or a 502 while the panel restarts (a deploy).
        if (stream.readyState !== EventSource.CLOSED || es !== stream) return;
        es = null;
        setState({ connected: false, problem: null });
        retry = setTimeout(recover, attempt ? Math.min(1000 * 2 ** attempt, 30_000) : 0);
        attempt += 1;
      };
    };

    // The stream can't say why it was refused; an ordinary request can (and signs in again when that's the reason).
    // Webmail answering again: reconnect, catching up. Down for now: ask again, less often each time.
    const recover = () => {
      void wmApi.get(`/m/${mb}/folders`).then(
        () => {
          if (!disposed && !es) connect(true);
        },
        (err: unknown) => {
          if (disposed || es) return;
          if (err instanceof ApiError && (err.status === 0 || err.code === "unavailable")) {
            retry = setTimeout(recover, Math.min(1000 * 2 ** attempt, 30_000));
            attempt += 1;
          } else void qc.invalidateQueries({ queryKey: ["wm", "session"] });
        },
      );
    };

    // A page kept in the back/forward cache would otherwise hold its stream open, and on HTTP/1.1 a few
    // of those use up the browser's six connections to the server, so new requests just wait. So the
    // stream closes when the page is put away and reopens, resyncing, when it comes back. (It stays open
    // in a background tab: that's when new-mail notifications and the tab's unread count matter.)
    const onHide = () => {
      clearTimeout(retry);
      es?.close();
      es = null;
    };
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted && !disposed && !es) connect(true);
    };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("pageshow", onShow);

    connect(false);
    return () => {
      disposed = true;
      clearTimeout(retry);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("pageshow", onShow);
      es?.close();
      es = null;
    };
  }, [mb, qc]);

  return state;
}
